import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Alert,
    IconButton,
    Paper,
    TextField,
    InputAdornment,
} from '@mui/material';
import {
    QrCodeScanner as QrCodeIcon,
    Close as CloseIcon,
    CameraAlt as CameraIcon,
    Search as SearchIcon,
    Send as SendIcon,
} from '@mui/icons-material';
import jsQR from 'jsqr';

const QRScanner = ({ open, onClose, onScan }) => {
    const [scanning, setScanning] = useState(false);
    const [error, setError] = useState('');
    const [manualCode, setManualCode] = useState('');
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);

    // Stop camera
    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            const tracks = streamRef.current.getTracks();
            tracks.forEach(track => track.stop());
            streamRef.current = null;
        }

        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }

        setScanning(false);
    }, []);

    // Start camera
    const startCamera = useCallback(async () => {
        try {
            setError('');
            stopCamera(); // Stop any existing stream

            const constraints = {
                video: {
                    facingMode: 'environment',
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                }
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.play();
                setScanning(true);
            }
        } catch (err) {
            console.error('Camera error:', err);
            setError(`Camera error: ${err.message}`);
            setScanning(false);
        }
    }, [stopCamera]);

    // Handle QR detection
    const handleQRDetected = useCallback((qrData) => {
        console.log('QR Code detected:', qrData);
        onScan(qrData.trim());
        onClose();
        stopCamera();
    }, [onClose, onScan, stopCamera]);

    // Scan QR Code using jsQR
    const scanQRCode = useCallback(() => {
        if (!videoRef.current || !canvasRef.current || !scanning) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // Ensure video is ready
        if (video.readyState !== video.HAVE_ENOUGH_DATA) return;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        // Use jsQR for QR code detection
        const code = jsQR(imageData.data, canvas.width, canvas.height, {
            inversionAttempts: "dontInvert",
        });

        if (code) {
            console.log('QR Code detected:', code.data);
            handleQRDetected(code.data);
        }
    }, [scanning, handleQRDetected]);

    // Handle manual QR entry
    const handleManualSubmit = () => {
        if (!manualCode.trim()) {
            setError('Please enter a QR code');
            return;
        }
        handleQRDetected(manualCode);
        setManualCode('');
    };

    // Auto-scan every second
    useEffect(() => {
        let interval;
        if (scanning) {
            interval = setInterval(scanQRCode, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [scanning, scanQRCode]);

    // Camera lifecycle
    useEffect(() => {
        if (open) {
            startCamera();
        } else {
            stopCamera();
            setManualCode('');
            setError('');
        }

        return () => {
            stopCamera();
        };
    }, [open, startCamera, stopCamera]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                }
            }}
        >
            <DialogTitle sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 2,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <QrCodeIcon sx={{ fontSize: 24 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Scan QR Code
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ py: 3 }}>
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3, borderRadius: 2 }}
                        onClose={() => setError('')}
                    >
                        {error}
                    </Alert>
                )}

                {/* Camera Preview with Square Overlay */}
                <Paper
                    sx={{
                        width: '100%',
                        aspectRatio: '4/3', // Standard camera aspect ratio
                        bgcolor: 'black',
                        borderRadius: 2,
                        overflow: 'hidden',
                        mb: 3,
                        position: 'relative',
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <video
                        ref={videoRef}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                    />

                    {/* SQUARE Scanning Overlay (Perfect Square) */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '70%', // Square width relative to container
                            aspectRatio: '1/1', // Makes it a perfect square
                            border: '3px solid',
                            borderColor: 'success.main',
                            borderRadius: 2,
                            pointerEvents: 'none',
                            boxShadow: '0 0 0 1000px rgba(0,0,0,0.7)',
                            animation: scanning ? 'pulse 2s infinite' : 'none',
                            '@keyframes pulse': {
                                '0%': { borderColor: '#00ff00', opacity: 1 },
                                '50%': { borderColor: '#00cc00', opacity: 0.7 },
                                '100%': { borderColor: '#00ff00', opacity: 1 },
                            },
                            zIndex: 1,
                        }}
                    >
                        {/* Corner markers for better visual guidance */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: 20,
                                height: 20,
                                borderTop: '3px solid',
                                borderLeft: '3px solid',
                                borderColor: 'success.main',
                                borderTopLeftRadius: 2,
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                width: 20,
                                height: 20,
                                borderTop: '3px solid',
                                borderRight: '3px solid',
                                borderColor: 'success.main',
                                borderTopRightRadius: 2,
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                width: 20,
                                height: 20,
                                borderBottom: '3px solid',
                                borderLeft: '3px solid',
                                borderColor: 'success.main',
                                borderBottomLeftRadius: 2,
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 0,
                                right: 0,
                                width: 20,
                                height: 20,
                                borderBottom: '3px solid',
                                borderRight: '3px solid',
                                borderColor: 'success.main',
                                borderBottomRightRadius: 2,
                            }}
                        />
                    </Box>

                    {/* Camera Status */}
                    {!scanning && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                textAlign: 'center',
                                color: 'white',
                                zIndex: 2,
                                backgroundColor: 'rgba(0,0,0,0.7)',
                                p: 3,
                                borderRadius: 2,
                            }}
                        >
                            <CameraIcon sx={{ fontSize: 48, mb: 2, opacity: 0.7 }} />
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                Camera starting...
                            </Typography>
                            <Typography variant="caption">
                                Please allow camera permissions
                            </Typography>
                        </Box>
                    )}
                </Paper>

                <canvas ref={canvasRef} style={{ display: 'none' }} />

                {/* Instructions */}
                <Alert
                    severity="info"
                    sx={{ mb: 3, borderRadius: 2 }}
                    icon={<QrCodeIcon />}
                >
                    <Typography variant="body2">
                        <strong>How to scan:</strong> Point the camera at the QR code on the jewelry label.
                        Make sure the entire QR code fits within the green square.
                    </Typography>
                </Alert>

                {/* Manual QR Entry */}
                <Paper
                    sx={{
                        p: 2.5,
                        borderRadius: 2,
                        backgroundColor: 'background.default',
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        gutterBottom
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                        <SearchIcon fontSize="small" />
                        Manual Entry
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Can't scan? Enter the QR code manually:
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter QR code (e.g., JWL1762407869023)"
                            value={manualCode}
                            onChange={(e) => setManualCode(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleManualSubmit();
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <QrCodeIcon fontSize="small" color="action" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                }
                            }}
                        />

                        <Button
                            variant="contained"
                            onClick={handleManualSubmit}
                            disabled={!manualCode.trim()}
                            startIcon={<SendIcon />}
                            sx={{
                                borderRadius: 2,
                                minWidth: '100px',
                                height: '40px',
                            }}
                        >
                            Add
                        </Button>
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        The QR code is printed on the jewelry label (e.g., "JWL1762407869023")
                    </Typography>
                </Paper>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                >
                    Cancel
                </Button>

                <Box sx={{ flex: 1 }} />

                {scanning ? (
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={stopCamera}
                        startIcon={<CloseIcon />}
                        sx={{ borderRadius: 2 }}
                    >
                        Stop Camera
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        onClick={startCamera}
                        startIcon={<CameraIcon />}
                        disabled={scanning}
                        sx={{ borderRadius: 2 }}
                    >
                        Start Camera
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default QRScanner;